import {
  BlockOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  DollarOutlined,
  EyeOutlined,
  MedicineBoxOutlined,
  TruckOutlined,
  CheckSquareOutlined,
  CommentOutlined,
  MessageOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { Menu, Skeleton, Space } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createUseStyles } from "react-jss";
import { useLocation, useNavigate } from "react-router-dom";
import { _STYLES } from "../../common/utils/customStyles";
import { useAuthContext } from "../contexts/AuthContextProvider";
import { ROUTES } from "../navigation";
import {
  MODULES,
  PERMISSION_FEATURES,
} from "../../permissions/types/permission-types";

const { textSm3, menuLoad, menuSkel } = _STYLES;
const useStyles = createUseStyles({
  textSm3,
  menuLoad,
  menuSkel,
});

const NavMenu = () => {
  const classes = useStyles();

  const [currentMenu, setCurrentMenu] = useState(ROUTES.dashboard);

  //used isAuthorizedToAccess instead of usePermission since the latter uses the same fn within the hook
  const {
    isAuthorizedToAccess,
    isPermissionLoading,
    allPermissions,
    isModuleLoading,
    isModuleEntitled,
  } = useAuthContext();

  const navigate = useNavigate();

  const currentLoc = useLocation();

  const navItems = useMemo(() => {
    const filterUnauthorized = (items) =>
      items
        .filter((item) => !item.disabled)
        .map((item) =>
          item.children
            ? { ...item, children: filterUnauthorized(item.children) }
            : item,
        );

    return filterUnauthorized([
      {
        key: ROUTES.dashboard,
        icon: <BlockOutlined />,
        label: <span className={classes.textSm3}>Dashboard</span>,
        disabled: !isAuthorizedToAccess(
          MODULES.DASHBOARD,
          PERMISSION_FEATURES.ENABLE,
        ),
      },
      {
        key: "appointmentsSub",
        label: <span className={classes.textSm3}>Appointments</span>,
        icon: <CalendarOutlined />,
        disabled: !isModuleEntitled(MODULES.APPOINTMENT),
        children: [
          {
            key: ROUTES.schedule,
            icon: <CheckSquareOutlined />,
            label: <span className={classes.textSm3}>Today's Schedule</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.APPOINTMENT,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
          {
            key: ROUTES.appointments,
            icon: <CalendarOutlined />,
            label: <span className={classes.textSm3}>Appointments</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.APPOINTMENT,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
          {
            key: ROUTES.billings,
            icon: <DollarOutlined />,
            label: <span className={classes.textSm3}>Billings</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.BILLING,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
        ],
      },

      {
        key: "patientsSub",
        label: <span className={classes.textSm3}>Patients</span>,
        icon: <MedicineBoxOutlined />,
        disabled: !isModuleEntitled(MODULES.PATIENT),
        children: [
          {
            key: ROUTES.patients,
            icon: <MedicineBoxOutlined />,
            label: <span className={classes.textSm3}>Patients</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.PATIENT,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
          {
            key: ROUTES.guardians,
            icon: <EyeOutlined />,
            label: <span className={classes.textSm3}>Guardians</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.GUARDIAN,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
        ],
      },

      {
        key: "inventorySub",
        icon: <DatabaseOutlined />,
        label: <span className={classes.textSm3}>Inventory</span>,
        disabled: !isModuleEntitled(MODULES.INVENTORY),
        children: [
          {
            key: ROUTES.inventory,
            icon: <DatabaseOutlined />,
            label: <span className={classes.textSm3}>Inventory</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.INVENTORY,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
          {
            key: ROUTES.suppliers,
            icon: <TruckOutlined />,
            label: <span className={classes.textSm3}>Suppliers</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.SUPPLIER,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
        ],
      },
      {
        key: "communicationSub",
        icon: <CommentOutlined />,
        label: <span className={classes.textSm3}>Communications</span>,
        disabled: !isModuleEntitled(MODULES.COMMUNICATIONS),
        children: [
          {
            key: ROUTES.smsMessages,
            icon: <MessageOutlined />,
            label: <span className={classes.textSm3}>SMS Messages</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.INVENTORY,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
          {
            key: ROUTES.logs,
            icon: <ProfileOutlined />,
            label: <span className={classes.textSm3}>Logs</span>,
            disabled: !isAuthorizedToAccess(
              MODULES.SUPPLIER,
              PERMISSION_FEATURES.ENABLE,
            ),
          },
        ],
      },
    ]);
  }, [classes.textSm3, isAuthorizedToAccess, isModuleEntitled]);

  const handleMenuSelection = useCallback(
    ({ key }) => {
      navigate(key);
    },
    [navigate],
  );

  useEffect(() => {
    if (currentLoc.pathname) {
      const currentPath = currentLoc.pathname.split("/")[1];
      setCurrentMenu(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLoc.pathname]);

  //Used to redirect the user to the dashboard when no URL routes are present
  useEffect(() => {
    if (!isPermissionLoading) {
      if (
        !currentLoc.pathname?.split("/")[1] &&
        isAuthorizedToAccess(MODULES.DASHBOARD, PERMISSION_FEATURES.ENABLE) &&
        Object.values(allPermissions).some((arr) => arr.length > 0) //ensure permissions are stored in state
      ) {
        navigate(ROUTES.dashboard);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermissions, currentLoc.pathname, isPermissionLoading]);

  return isPermissionLoading || isModuleLoading ? (
    <Space size={8} className={classes.menuLoad} direction="vertical">
      <Skeleton.Input active className={classes.menuSkel} block />
      <Skeleton.Input active className={classes.menuSkel} block />
      <Skeleton.Input active className={classes.menuSkel} block />
    </Space>
  ) : (
    <Menu
      onSelect={handleMenuSelection}
      selectedKeys={[currentMenu]}
      defaultSelectedKeys={[ROUTES.dashboard]}
      items={navItems}
      mode="inline"
    />
  );
};

export default NavMenu;
